import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Reorder } from 'framer-motion';
import { OptionsListContainer } from '../Styles';
import { DraggableOption } from './DraggableOption';
import { Option } from './types';

interface OptionsListProps {
  selectedOptions: Option[];
  setSelectedOptions: (options: Option[]) => void;
  handleEditOption: (option: Option, isSoldOut?: boolean) => void;
  handleOptionRemove: (optionId: number | string) => void;
  optionsBorderColor: string;
  selectedItem: number | null;
  soldOutLoading?: number | string | null;
  onOrderChange?: (draggedOption: Option, fromIndex: number, toIndex: number, displacedOption?: Option) => void;
}

export const OptionsList: React.FC<OptionsListProps> = ({
  selectedOptions,
  setSelectedOptions,
  handleEditOption,
  handleOptionRemove,
  optionsBorderColor,
  selectedItem,
  soldOutLoading,
  onOrderChange,
}) => {
  // Track order of options by ID (similar to MerchantCategories)
  const [optionOrder, setOptionOrder] = useState<(number | string)[]>([]);

  // Initialize optionOrder when selectedOptions changes
  useEffect(() => {
    const sortedOptions = [...selectedOptions]
      .filter((option) => option?.id)
      .sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));
    
    const newOrder = sortedOptions.map((opt) => opt.id);
    setOptionOrder(newOrder);
  }, [selectedOptions]);
  
  // Handle reorder of options (similar to MerchantCategories)
  const handleReorder = useCallback(async (newOrder: (number | string)[]) => {
    // Store previous order before updating
    const previousOrder = [...optionOrder];
    setOptionOrder(newOrder);

    // Update options with new sequence numbers
    const reorderedOptions = newOrder.map((id, index) => {
      const option = selectedOptions.find((opt) => opt.id === id);
      if (!option) return null;
      return {
        ...option,
        sequence_number: index,
      };
    }).filter(Boolean) as Option[];

    // Update local state immediately for visual feedback
    setSelectedOptions(reorderedOptions);

    // Send updated sequence numbers to the backend
    if (onOrderChange && reorderedOptions.length > 0) {
      try {
        // Find the dragged item and displaced item
        const draggedId = newOrder.find((id, index) => previousOrder[index] !== id);
        if (draggedId) {
          const draggedIndex = previousOrder.findIndex((id) => id === draggedId);
          const newIndex = newOrder.findIndex((id) => id === draggedId);

          if (draggedIndex !== -1 && newIndex !== -1 && draggedIndex !== newIndex) {
            const draggedOption = reorderedOptions.find((opt) => opt.id === draggedId);
            const displacedOption = reorderedOptions.find(
              (opt) => opt.id === previousOrder[newIndex]
            );

            if (draggedOption) {
              await onOrderChange(
                draggedOption,
                draggedIndex,
                newIndex,
                displacedOption
              );
            }
          }
        }
      } catch (error) {
        console.error('Error updating sequence numbers:', error);
      }
    }
  }, [selectedOptions, optionOrder, onOrderChange, setSelectedOptions]);

  // Get options in the correct order
  const orderedOptions = useMemo(() => {
    return optionOrder
      .map((id) => selectedOptions.find((opt) => opt.id === id))
      .filter(Boolean) as Option[];
  }, [optionOrder, selectedOptions]);

  return (
    <OptionsListContainer>
      <Reorder.Group
        axis="y"
        values={optionOrder}
        onReorder={handleReorder}
        style={{ listStyle: 'none', padding: 0, margin: 0 }}
      >
        {orderedOptions.map((option) => (
          <DraggableOption
            key={option.id}
            option={option}
            value={option.id}
            handleEditOption={handleEditOption}
            handleOptionRemove={handleOptionRemove}
            optionsBorderColor={optionsBorderColor}
            soldOutLoading={soldOutLoading}
          />
        ))}
      </Reorder.Group>
    </OptionsListContainer>
  );
};

